import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import PasswordField from '../../components/PasswordField';
import { useAuth } from '../../contexts/AuthContext';
import * as invitationApi from '../../services/invitationApi';
import usePageTitle from '../../hooks/usePageTitle';

export default function AcceptInvite() {
  usePageTitle('Accept Invitation');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoadError('Missing invitation token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await invitationApi.getInvitation(token);
      if (cancelled) return;
      if (result.ok) {
        setDetails(result.data);
      } else {
        setLoadError(result.error || 'Invitation not found.');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async () => {
    setSubmitError('');
    if (details.isNewUser) {
      if (!password || password.length < 8) {
        setSubmitError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirm) {
        setSubmitError('Passwords do not match.');
        return;
      }
    }
    setSubmitting(true);
    const result = await invitationApi.acceptInvitation(token, details.isNewUser ? password : null);
    setSubmitting(false);
    if (result.ok) {
      await refreshUser();
      navigate('/');
    } else {
      setSubmitError(result.error || 'Failed to accept invitation.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="sm">
        <Card variant="outlined">
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h5" fontWeight={600}>Accept Invitation</Typography>
            </Box>

            {loadError ? (
              <Alert severity="error">{loadError}</Alert>
            ) : (
              <>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You've been invited to join <strong>{details.organisationName}</strong> as a <strong>{details.role}</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Invitation for {details.email}.
                </Typography>

                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

                {details.isNewUser ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Set a password for your new account.
                    </Typography>
                    <PasswordField
                      label="Password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <PasswordField
                      label="Confirm password"
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click below to accept and join the organisation. You'll be signed in immediately.
                  </Typography>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleAccept}
                    disabled={submitting}
                  >
                    {submitting ? <CircularProgress size={20} color="inherit" /> : `Join ${details.organisationName}`}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
